// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from './api.service';
import { Lead, LeadStatus } from '@interfaces/lead.interfaces';
import { PaginatedData } from '@interfaces/interfaces';

@Injectable({
    providedIn: 'root',
})
export class LeadsService {
    private readonly PAGE_SIZE_KEY = 'leadsTablePageSize';

    constructor(private readonly apiService: ApiService) {}

    getTablePageSize(): number {
        const pageSize = sessionStorage.getItem(this.PAGE_SIZE_KEY);
        return pageSize ? parseInt(pageSize, 10) : 25;
    }

    setTablePageSize(size: number): void {
        sessionStorage.setItem(this.PAGE_SIZE_KEY, size.toString());
    }

    getLeads$(
        dealerId: number,
        search = '',
        page = 1,
        records = 25,
        categories: LeadStatus[] = [LeadStatus.Open],
        sortBy: string = '',
        sortAsc: boolean = true
    ): Observable<PaginatedData & { leads?: Lead[] }> {
        const statusIds = categories.join(',');
        const pageNumber = page - 1; // Adjusting page number to be zero-based for API
        return this.apiService
            .getV2$<
                PaginatedData & { leads?: Lead[] }
            >(`api/leads/${dealerId}?search=${search}&page=${pageNumber}&records=${records}&categories=${statusIds}&sortBy=${sortBy}&sortAsc=${sortAsc}`)
            .pipe(
                map((response) => {
                    return response?.data ?? {};
                })
            );
    }

    getLead$(dealerId: number, leadId: number): Observable<any> {
        return this.apiService.getV2$(`api/leads/${dealerId}/${leadId}`).pipe(map((response) => response?.data));
    }

    updateLeadStatus$(dealerId: number, leadId: number, leadStatusId: LeadStatus) {
        return this.apiService
            .putV3$('api/leads/updateStatus', {
                dealerId,
                leadId,
                leadStatusId,
            })
            .pipe(
                map((response) => {
                    if (response?.data) {
                        return response.data;
                    } else {
                        return undefined;
                    }
                })
            );
    }

    updateLeadVehicle$(lead: Lead) {
        return this.apiService.putV2$('api/leads/updateVehicle', lead).pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return undefined;
                }
            })
        );
    }

    updateLeadTradeIn$(lead: Lead) {
        return this.apiService.putV2$('api/leads/updateTradeIn', lead).pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return undefined;
                }
            })
        );
    }

    updateLeadPaymentInfo$(lead: Lead) {
        return this.apiService.putV2$('api/leads/updatePayment', lead).pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return undefined;
                }
            })
        );
    }

    updateLeadCustomer$(lead: Lead) {
        return this.apiService.putV2$('api/leads/updateCustomer', lead).pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return undefined;
                }
            })
        );
    }

    updateLeadNotes$(lead: Lead) {
        return this.apiService.putV2$('api/leads/updateNotes', lead).pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return undefined;
                }
            })
        );
    }
}
